import { landingFeaturedMarkets } from "../features/landing/components/constants/content";
import {
	createTradeMarketIdentity,
	createTradeRouteSymbol,
	KNOWN_MARKET_NAMES,
	XYZ_DEX_NAME,
} from "../features/trade/lib/hyperliquid";
import {
	getLocalizedPerpetualDisplayName,
	type MarketTranslate,
} from "../features/trade/lib/market-display";
import { XYZ_MARKET_NAMES } from "../features/trade/lib/xyz-markets";
import type { Post } from "./api/posts";
import {
	type SupportedLocale,
	stripLocalePrefix,
	withLocalePrefix,
} from "./locale";
import { deriveMetaDescription, stripMarkdown } from "./markdown-plain";

export const SITE_NAME = "Tenbagger";
// Korean brand spelling — must match Organization.alternateName in index.html.
export const SITE_NAME_KO = "텐배거";

function siteNameForLocale(locale: SupportedLocale) {
	return locale === "ko" ? SITE_NAME_KO : SITE_NAME;
}
export const SITE_URL = "https://tenbagger.xyz";
export const DEFAULT_SOCIAL_IMAGE = "/images/tenbagger_preview.jpg";
export const DEFAULT_TITLE = `${SITE_NAME} | Global Perpetual Trading`;
export const DEFAULT_DESCRIPTION =
	"Trade crypto and global perpetual futures on Tenbagger. Track markets, manage positions, and learn the platform in one interface.";

/**
 * learn 글 SEO의 데이터 심 — 글 페이지가 learnSeoAtom에 게시한 fetch 결과.
 * `post: null` = 404(미발행/없는 slug), 심 자체가 null = 아직 로딩/비-learn 경로.
 */
export type LearnSeo = { slug: string; post: Post | null } | null;

type SeoHeadOptions = {
	title: string;
	description: string;
	path: string;
	robots?: string;
};

export type RuntimeSeoContent = {
	title: string;
	description: string;
	path: string;
	robots: string;
};

function normalizePath(path: string) {
	if (!path) return "/";
	const prefixed = path.startsWith("/") ? path : `/${path}`;
	// Trailing-slash variants (`/learn/usdc/`) must converge on the slash-less
	// canonical — Google crawls both forms and CF Pages 308s the slashed one.
	const stripped = prefixed.replace(/\/+$/, "");
	return stripped === "" ? "/" : stripped;
}

export function getCanonicalUrl(path: string) {
	return new URL(normalizePath(path), SITE_URL).toString();
}

/**
 * Whether a logical path has localized (`/ko/...`) URLs. Phase 1: only landing +
 * learn — so hreflang/sitemap never advertise a /ko trade URL that would 404.
 */
function isLocalizablePath(logicalPath: string): boolean {
	if (logicalPath === "/" || logicalPath === "/learn") return true;
	// slug 목록은 이제 CMS(API)에만 있어 정적으로 알 수 없음 — prefix 매치로 완화.
	// 존재하지 않는 slug도 /ko 경로를 얻지만 그 페이지는 noindex라 무해.
	if (logicalPath.startsWith("/learn/")) return true;
	return false;
}

export function buildSeoHead({
	title,
	description,
	path,
	robots = "index, follow",
}: SeoHeadOptions) {
	const canonical = getCanonicalUrl(path);
	const image = getCanonicalUrl(DEFAULT_SOCIAL_IMAGE);

	return {
		meta: [
			{ title },
			{ name: "description", content: description },
			{ name: "robots", content: robots },
			{ property: "og:type", content: "website" },
			{ property: "og:site_name", content: SITE_NAME },
			{ property: "og:title", content: title },
			{ property: "og:description", content: description },
			{ property: "og:url", content: canonical },
			{ property: "og:image", content: image },
			{ name: "twitter:card", content: "summary" },
			{ name: "twitter:title", content: title },
			{ name: "twitter:description", content: description },
			{ name: "twitter:image", content: image },
		],
		links: [{ rel: "canonical", href: canonical }],
	};
}

export function buildTradeSeoHead(routeSymbol: string) {
	const market = createTradeMarketIdentity(routeSymbol);
	const title = `${market.displaySymbol} | ${SITE_NAME}`;
	const description = `Trade ${market.displayName} on ${SITE_NAME}. Track charts, order flow, positions, and perpetual market activity from a single workspace.`;

	return buildSeoHead({
		title,
		description,
		path: `/trade/${market.routeSymbol}`,
	});
}

export const PUBLIC_SITEMAP_PATHS = ["/", "/learn", "/baggers"] as const;

export const PUBLIC_TRADE_SITEMAP_SYMBOLS = Array.from(
	new Set([
		...Object.keys(KNOWN_MARKET_NAMES).map((coin) =>
			createTradeRouteSymbol(coin),
		),
		...Object.keys(XYZ_MARKET_NAMES).map((coin) =>
			createTradeRouteSymbol(coin, XYZ_DEX_NAME),
		),
		...landingFeaturedMarkets.map((market) => market.routeSymbol),
	]),
).sort((left, right) => left.localeCompare(right));

/** learn slug 목록은 빌드 타임에 /v1/posts에서 fetch해 주입한다 (scripts/lib/learn-posts.ts). */
export function getPublicSitemapPaths(learnSlugs: readonly string[]) {
	return [
		...PUBLIC_SITEMAP_PATHS,
		...learnSlugs.map((slug) => `/learn/${slug}`),
		...PUBLIC_TRADE_SITEMAP_SYMBOLS.map((symbol) => `/trade/${symbol}`),
	];
}

export type SitemapUrlEntry = {
	/** Logical (locale-stripped) path — drives changefreq/priority. */
	logicalPath: string;
	/** Absolute canonical URL for this `<loc>`. */
	loc: string;
	/** hreflang alternates (empty for non-localized pages). */
	alternates: Array<{ hreflang: string; href: string }>;
};

/**
 * Sitemap entries with hreflang alternates. Localized paths (Phase 1: landing +
 * learn) emit both en + ko `<url>` rows, each carrying en/ko/x-default
 * alternates; everything else stays a single en row.
 */
export function getSitemapUrlEntries(
	learnSlugs: readonly string[],
): SitemapUrlEntry[] {
	const entries: SitemapUrlEntry[] = [];
	for (const path of getPublicSitemapPaths(learnSlugs)) {
		if (isLocalizablePath(path)) {
			const enHref = getCanonicalUrl(path);
			const koHref = getCanonicalUrl(withLocalePrefix(path, "ko"));
			const alternates = [
				{ hreflang: "en", href: enHref },
				{ hreflang: "ko", href: koHref },
				{ hreflang: "x-default", href: enHref },
			];
			entries.push({ logicalPath: path, loc: enHref, alternates });
			entries.push({ logicalPath: path, loc: koHref, alternates });
		} else {
			entries.push({
				logicalPath: path,
				loc: getCanonicalUrl(path),
				alternates: [],
			});
		}
	}
	return entries;
}

function decodeTradeSymbolFromPath(pathname: string) {
	return decodeURIComponent(pathname.slice("/trade/".length));
}

function getDefaultRuntimeSeo(t: MarketTranslate): RuntimeSeoContent {
	return {
		title: t("seo.home.title"),
		description: t("seo.home.description"),
		path: "/",
		robots: "index, follow",
	};
}

export function getRuntimeSeoContent({
	pathname,
	t,
	locale = "en",
	learnSeo = null,
}: {
	pathname: string;
	t: MarketTranslate;
	locale?: SupportedLocale;
	learnSeo?: LearnSeo;
}): RuntimeSeoContent {
	// Resolve on the logical (prefix-stripped) path, then re-apply the locale
	// prefix so `/ko/...` pages self-canonicalize — but ONLY for paths that
	// actually have localized URLs. Prefixing a non-localizable path (/terms,
	// /sign, /trade/*, /portfolio, /referrals) would canonicalize to a /ko/...
	// URL that 404s, so those keep a single (English) canonical for every locale.
	// normalizePath first: a trailing slash would miss every `===` branch below
	// and fall through to the home defaults (canonical `/`, noindex lost).
	const base = resolveRuntimeSeo(
		stripLocalePrefix(normalizePath(pathname)),
		t,
		locale,
		learnSeo,
	);
	const path = isLocalizablePath(base.path)
		? withLocalePrefix(base.path, locale)
		: base.path;
	return { ...base, path };
}

function resolveRuntimeSeo(
	pathname: string,
	t: MarketTranslate,
	locale: SupportedLocale,
	learnSeo: LearnSeo,
): RuntimeSeoContent {
	if (pathname === "/learn") {
		return {
			title: t("seo.learn.title"),
			description: t("seo.learn.description"),
			path: pathname,
			robots: "index, follow",
		};
	}

	if (pathname.startsWith("/learn/")) {
		const slug = pathname.slice("/learn/".length);
		// 글 데이터는 API에서 오고 learnSeoAtom을 통해 여기 도착한다 (learn-seo-store).
		if (learnSeo && learnSeo.slug === slug) {
			if (learnSeo.post) {
				return {
					title: `${learnSeo.post.title} | ${siteNameForLocale(locale)}`,
					description: deriveMetaDescription(learnSeo.post.body),
					path: `/learn/${slug}`,
					robots: "index, follow",
				};
			}
			// 미발행/없는 slug — self-canonical + noindex (홈 폴백으로 인덱스되는 것 방지)
			return {
				title: `${t("learn.guide.not-found")} | ${siteNameForLocale(locale)}`,
				description: t("seo.learn.description"),
				path: `/learn/${slug}`,
				robots: "noindex, follow",
			};
		}
		// fetch 전 플레이스홀더 — prerender는 networkidle 이후 최종 분기를 캡처한다
		return {
			title: t("seo.learn.title"),
			description: t("seo.learn.description"),
			path: `/learn/${slug}`,
			robots: "index, follow",
		};
	}

	if (pathname === "/portfolio") {
		return {
			title: t("seo.portfolio.title"),
			description: t("seo.portfolio.description"),
			path: pathname,
			robots: "noindex, nofollow",
		};
	}

	if (pathname === "/referrals") {
		return {
			title: t("seo.referrals.title"),
			description: t("seo.referrals.description"),
			path: pathname,
			robots: "noindex, nofollow",
		};
	}

	// /terms + /sign are English-only static pages (no Tolgee, no /ko URL). They
	// self-canonicalize with hardcoded metadata instead of falling through to the
	// home defaults (which would canonicalize them to `/` — a duplicate of home).
	if (pathname === "/terms") {
		return {
			title: `Terms of Service | ${SITE_NAME}`,
			description:
				"Tenbagger Terms of Service. A front-end interface to the Hyperliquid DEX — Tenbagger holds no custody, runs no exchange, and gives no financial advice.",
			path: "/terms",
			robots: "index, follow",
		};
	}

	if (pathname === "/sign") {
		return {
			title: `Sign Message | ${SITE_NAME}`,
			description:
				"Sign a message with your Tenbagger wallet to verify wallet ownership.",
			path: "/sign",
			robots: "noindex, nofollow",
		};
	}

	if (pathname === "/baggers") {
		return {
			title: t("seo.baggers.title"),
			description: t("seo.baggers.description"),
			path: "/baggers",
			robots: "index, follow",
		};
	}

	if (pathname.startsWith("/trade/")) {
		const routeSymbol = decodeTradeSymbolFromPath(pathname);
		const market = createTradeMarketIdentity(routeSymbol);
		const localizedMarketName = getLocalizedPerpetualDisplayName({
			coin: market.coin,
			dex: market.dex,
			t,
		});

		return {
			title: t("seo.trade.title", { symbol: market.displaySymbol }),
			description: t("seo.trade.description", {
				market: localizedMarketName,
			}),
			path: `/trade/${market.routeSymbol}`,
			robots: "index, follow",
		};
	}

	return getDefaultRuntimeSeo(t);
}

function getOgLocale(locale: SupportedLocale) {
	switch (locale) {
		case "ko":
			return "ko_KR";
		case "zh":
			return "zh_CN";
		default:
			return "en_US";
	}
}

function upsertMeta({
	attribute,
	key,
	content,
}: {
	attribute: "name" | "property";
	key: string;
	content: string;
}) {
	const existing = Array.from(
		document.head.querySelectorAll<HTMLMetaElement>(`meta[${attribute}]`),
	).find((element) => element.getAttribute(attribute) === key);

	const meta = existing ?? document.createElement("meta");
	meta.setAttribute(attribute, key);
	meta.setAttribute("content", content);

	if (!existing) {
		document.head.appendChild(meta);
	}
}

function upsertCanonicalLink(href: string) {
	const existing = document.head.querySelector<HTMLLinkElement>(
		'link[rel="canonical"]',
	);
	const link = existing ?? document.createElement("link");
	link.setAttribute("rel", "canonical");
	link.setAttribute("href", href);

	if (!existing) {
		document.head.appendChild(link);
	}
}

// Emit hreflang alternates from the LOGICAL path. Localized pages (Phase 1:
// landing + learn) get en + ko + x-default, each pointing at its own locale URL
// (self-canonical). Non-localized pages stay en-only — we never advertise a
// /ko URL that 404s.
function syncHreflangLinks(logicalPath: string) {
	for (const node of document.head.querySelectorAll<HTMLLinkElement>(
		'link[rel="alternate"][hreflang]',
	)) {
		node.remove();
	}

	const enHref = getCanonicalUrl(logicalPath);
	const alternates: Array<{ hreflang: string; href: string }> = [
		{ hreflang: "en", href: enHref },
	];
	if (isLocalizablePath(logicalPath)) {
		alternates.push({
			hreflang: "ko",
			href: getCanonicalUrl(withLocalePrefix(logicalPath, "ko")),
		});
		alternates.push({ hreflang: "x-default", href: enHref });
	}

	for (const { hreflang, href } of alternates) {
		const link = document.createElement("link");
		link.setAttribute("rel", "alternate");
		link.setAttribute("hreflang", hreflang);
		link.setAttribute("href", href);
		document.head.appendChild(link);
	}
}

const ROUTE_JSON_LD_ID = "route-json-ld";

function upsertRouteJsonLd(payload: object | null) {
	const existing = document.head.querySelector<HTMLScriptElement>(
		`script[data-seo-id="${ROUTE_JSON_LD_ID}"]`,
	);

	if (payload === null) {
		existing?.remove();
		return;
	}

	const script = existing ?? document.createElement("script");
	script.setAttribute("type", "application/ld+json");
	script.dataset.seoId = ROUTE_JSON_LD_ID;
	script.textContent = JSON.stringify(payload);

	if (!existing) {
		document.head.appendChild(script);
	}
}

export function buildTradeJsonLd({
	pathname,
	title,
	description,
}: {
	pathname: string;
	title: string;
	description: string;
}) {
	const routeSymbol = decodeTradeSymbolFromPath(pathname);
	const market = createTradeMarketIdentity(routeSymbol);
	const canonical = getCanonicalUrl(`/trade/${market.routeSymbol}`);

	return {
		"@context": "https://schema.org",
		"@type": "FinancialProduct",
		name: title,
		description,
		url: canonical,
		category: "Perpetual Futures",
		provider: { "@id": `${SITE_URL}/#organization` },
		tickerSymbol: market.displaySymbol,
	};
}

// FAQPage 노드는 폐기 — FAQ가 CMS 이관에서 본문 md로 평탄화되어 구조화 원천이
// 사라졌고, 구글도 2023년부터 일반 사이트에는 FAQ 리치결과를 표시하지 않는다.
// (부활 시: 여기서 post.body의 `## 질문\n\n답변` 섹션을 재파싱해 @graph로 붙일 것.
// articleBody가 평탄화된 Q&A 텍스트를 그대로 담고 있어 인용 신호는 유지된다.)
export function buildLearnArticleJsonLd({
	post,
	locale,
}: {
	post: Post;
	locale: SupportedLocale;
}) {
	const url = getCanonicalUrl(withLocalePrefix(`/learn/${post.slug}`, locale));
	const image = getCanonicalUrl(DEFAULT_SOCIAL_IMAGE);

	return {
		"@context": "https://schema.org",
		"@type": "Article",
		mainEntityOfPage: { "@type": "WebPage", "@id": url },
		url,
		headline: post.title,
		description: deriveMetaDescription(post.body),
		articleBody: stripMarkdown(post.body),
		image,
		// CMS의 실제 타임스탬프 — 전역 상수(LEARN_ARTICLES_*_AT) 대비 업그레이드
		datePublished: post.created_at,
		dateModified: post.updated_at,
		author: {
			"@type": "Organization",
			name: SITE_NAME,
			url: SITE_URL,
		},
		publisher: { "@id": `${SITE_URL}/#organization` },
	};
}

export function buildLearnHubCollectionJsonLd(
	t: MarketTranslate,
	locale: SupportedLocale = "en",
) {
	const url = getCanonicalUrl(withLocalePrefix("/learn", locale));
	return {
		"@context": "https://schema.org",
		"@type": "CollectionPage",
		"@id": url,
		url,
		name: t("seo.learn.title"),
		description: t("seo.learn.description"),
		isPartOf: { "@id": `${SITE_URL}/#website` },
		// ItemList 제거 — 엔트리 목록의 정적 원천이 CMS로 이동. URL 노출은
		// 사이트맵 + prerender가 담당한다.
	};
}

export function syncDocumentSeo({
	locale,
	pathname,
	t,
	learnSeo = null,
}: {
	locale: SupportedLocale;
	pathname: string;
	t: MarketTranslate;
	learnSeo?: LearnSeo;
}) {
	if (typeof document === "undefined") return;

	const seo = getRuntimeSeoContent({ pathname, t, locale, learnSeo });
	// Same normalization as getRuntimeSeoContent — hreflang + JSON-LD branch on
	// this path, and a trailing slash would drop ko alternates and the JSON-LD.
	const logicalPath = stripLocalePrefix(normalizePath(pathname));
	const canonical = getCanonicalUrl(seo.path);
	const image = getCanonicalUrl(DEFAULT_SOCIAL_IMAGE);

	document.title = seo.title;
	upsertMeta({
		attribute: "name",
		key: "description",
		content: seo.description,
	});
	upsertMeta({
		attribute: "name",
		key: "robots",
		content: seo.robots,
	});
	upsertMeta({
		attribute: "property",
		key: "og:type",
		content: "website",
	});
	upsertMeta({
		attribute: "property",
		key: "og:site_name",
		content: SITE_NAME,
	});
	upsertMeta({
		attribute: "property",
		key: "og:title",
		content: seo.title,
	});
	upsertMeta({
		attribute: "property",
		key: "og:description",
		content: seo.description,
	});
	upsertMeta({
		attribute: "property",
		key: "og:url",
		content: canonical,
	});
	upsertMeta({
		attribute: "property",
		key: "og:image",
		content: image,
	});
	upsertMeta({
		attribute: "property",
		key: "og:image:type",
		content: "image/jpeg",
	});
	upsertMeta({
		attribute: "property",
		key: "og:image:width",
		content: "1200",
	});
	upsertMeta({
		attribute: "property",
		key: "og:image:height",
		content: "630",
	});
	upsertMeta({
		attribute: "property",
		key: "og:image:alt",
		content: seo.title,
	});
	upsertMeta({
		attribute: "property",
		key: "og:locale",
		content: getOgLocale(locale),
	});
	upsertMeta({
		attribute: "name",
		key: "twitter:card",
		content: "summary_large_image",
	});
	upsertMeta({
		attribute: "name",
		key: "twitter:title",
		content: seo.title,
	});
	upsertMeta({
		attribute: "name",
		key: "twitter:description",
		content: seo.description,
	});
	upsertMeta({
		attribute: "name",
		key: "twitter:image",
		content: image,
	});
	upsertCanonicalLink(canonical);
	syncHreflangLinks(logicalPath);

	if (logicalPath.startsWith("/trade/")) {
		upsertRouteJsonLd(
			buildTradeJsonLd({
				pathname: logicalPath,
				title: seo.title,
				description: seo.description,
			}),
		);
	} else if (logicalPath.startsWith("/learn/")) {
		const slug = logicalPath.slice("/learn/".length);
		upsertRouteJsonLd(
			learnSeo && learnSeo.slug === slug && learnSeo.post
				? buildLearnArticleJsonLd({ post: learnSeo.post, locale })
				: null,
		);
	} else if (logicalPath === "/learn") {
		upsertRouteJsonLd(buildLearnHubCollectionJsonLd(t, locale));
	} else {
		upsertRouteJsonLd(null);
	}
}
